import { useQueryGetUserMe } from '../queries/userQueries'
import { useQueryGetOrganizationSubscriptions } from '../queries/paymentQueries'
import { useGlobalContext } from '../context/GlobalState'

export interface PlanPermissions {
  allowMultiUsers: boolean
  allowBukIntegration: boolean
  allowMercadopagoIntegration: boolean
  allowGetnetMovements: boolean
  allowTransbankMovements: boolean
  allowCostCenter: boolean
  allowIntelligentBilling: boolean
  allowAccountingDesk: boolean
  maxBusinesses: number
  maxBanks: number
  maxTokens: number
  maxMovements: number
  allowedSupports: string[]
  allowedReports: string[]
  allowedDashboards: string[]
}

export const usePlanPermissions = () => {
  const { currentBusiness } = useGlobalContext()
  const { data: userMe } = useQueryGetUserMe()
  const organizationId = Number(currentBusiness?.organizationId || 0)
  const { data: organizationSubscriptions } = useQueryGetOrganizationSubscriptions(
    organizationId > 0 ? organizationId : null
  )
  const plan = userMe?.plan

  const getAddonExtraBanksAmount = (): number => {
    const addonSubscriptions = organizationSubscriptions?.addonSubscriptions
    if (!Array.isArray(addonSubscriptions) || addonSubscriptions.length === 0) return 0

    return addonSubscriptions.reduce((total: number, addonSubscription: any) => {
      const isActive = Boolean(addonSubscription?.isActive ?? addonSubscription?.is_active ?? false)
      const isExpired = Boolean(addonSubscription?.isExpired ?? addonSubscription?.is_expired ?? false)
      if (!isActive || isExpired) return total

      const addonType = String(
        addonSubscription?.addon?.addonType ??
          addonSubscription?.addon?.addon_type ??
          addonSubscription?.addonType ??
          addonSubscription?.addon_type ??
          ''
      ).toLowerCase()

      const isExtraBanksAddon = addonType === 'extra_banks_amount' || addonType === 'extra_banks'
      if (!isExtraBanksAddon) return total

      const rawAmount = addonSubscription?.extraBanksAmount ?? addonSubscription?.extra_banks_amount ?? 0
      const parsedAmount = Number(rawAmount)
      return total + (Number.isFinite(parsedAmount) && parsedAmount > 0 ? parsedAmount : 0)
    }, 0)
  }

  const hasPermission = (permission: keyof PlanPermissions): boolean => {
    if (!plan) return false
    
    if (permission === 'allowMultiUsers') return plan.allowMultiUsers
    if (permission === 'allowBukIntegration') return plan.allowBukIntegration
    if (permission === 'allowMercadopagoIntegration') return plan.allowMercadopagoIntegration
    if (permission === 'allowGetnetMovements') return plan.allowGetnetMovements
    if (permission === 'allowTransbankMovements') return plan.allowTransbankMovements
    if (permission === 'allowCostCenter') return plan.allowCostCenter
    if (permission === 'allowIntelligentBilling') return plan.allowIntelligentBilling
    if (permission === 'allowAccountingDesk') return plan.allowAccountingDesk
    
    return false
  }

  const hasSupportAccess = (supportType: string): boolean => {
    if (!plan) return false
    return plan.allowedSupports.includes(supportType)
  }

  const hasReportAccess = (reportType: string): boolean => {
    if (!plan) return false
    return plan.allowedReports.includes(reportType)
  }

  const hasDashboardAccess = (dashboardType: string): boolean => {
    if (!plan) return false
    return plan.allowedDashboards.includes(dashboardType)
  }

  const getLimit = (limitType: 'maxBusinesses' | 'maxBanks' | 'maxTokens' | 'maxMovements'): number => {
    if (!plan) return 0
    return plan[limitType] || 0
  }

  const canAddBusiness = (): boolean => {
    if (!plan || !userMe) return false
    const businessesCount = userMe.businessesCount || 0
    const maxBusinesses = plan.maxBusinesses || 0
    return businessesCount < maxBusinesses
  }

  const canAddBank = (): boolean => {
    if (!plan || !userMe) return false
    const banksCount = userMe.banksCount ?? 0
    const extraBanksFromAddons = getAddonExtraBanksAmount()
    const maxBanks = (plan.maxBanks ?? 0) + extraBanksFromAddons
    return banksCount < maxBanks
  }

  const canAccessReport = (reportId: string): boolean => {
    if (!plan) return false

    if (reportId === '2') {
      return plan.allowedReports.includes('basic')
    }

    if (['3', '4', '6'].includes(reportId)) {
      return plan.allowedReports.includes('financial')
    }

    if (['5', '8', '9', '11', '12', '13'].includes(reportId)) {
      return plan.allowedReports.includes('accounting')
    }

    return true
  }

  const canAccessDashboard = (dashboardType: 'basic' | 'full'): boolean => {
    if (!plan) return false

    return plan.allowedDashboards.includes(dashboardType)
  }

  const isTrialPlan = plan?.isTrial === true
  const canContractAddons = Boolean(plan) && !isTrialPlan

  return {
    plan,
    userMe,
    isTrialPlan,
    canContractAddons,
    hasPermission,
    hasSupportAccess,
    hasReportAccess,
    hasDashboardAccess,
    getLimit,
    canAddBusiness,
    canAddBank,
    canAccessReport,
    canAccessDashboard,
    subscriptionExpired: userMe?.subscriptionExpired || false,
    hasPlan: !!plan
  }
}
